<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="false"
                title="Partial Return Hotel Bill"
                @onClickCloseButton="goToList"
            />
        </div>

        <div class="my-2 px-2">
            <HotelReturnBillHeader :guest="guest"/>
        </div>

        <div class="col-12 px-2">
            <ReturnPartialTable
                :tableItems="tableItems"
                :salesAccountHeads="salesAccountHeads"
                :is-hide-add-btn="true"
                :from-return="true"
            />
        </div>

        <div class="row px-2">
            <div class="col-md-6">
                <div class="mb-1">
                    <label class="form-label" for="description">Ledger Memo</label>
                    <vField
                        as="textarea"
                        name="description"
                        class="form-control"
                        v-model="formData.description"
                    />
                </div>
            </div>
            <div class="col-md-5 ml-auto mt-auto mb-1">
                <div class="d-flex flex-wrap gap-1 gy-2">
                    <button @click="handleSubmit" class="btn btn-primary" :disabled="loader">Return Bill</button>
                </div>
            </div>
        </div>

        <Loader v-if="loader"/>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, provide, reactive, ref, watch } from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import Loader from '@/components/atom/LoaderComponent';
import ReturnPartialTable from '@/components/molecule/company/hotel/hotel-bill-return/ReturnIpdPartialTable.vue';
import HotelReturnBillHeader from '@/components/molecule/company/hotel/hotel-bill-return/HotelReturnBillHeader.vue';
import { useRoute, useRouter } from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import handleCompany from '@/services/modules/company';
import handleHospital from '@/services/modules/hospital';
import useDate from '@/services/utils/day';
import handleHospitalBilling from '@/services/modules/hospital/billing';
import salesReturnHelper from '@/services/utils/pdf/salesReturnHelper';

const { fetchSalesBill, ...rest } = handlePurchase();
const { fetchCompanyInfo } = handleCompany();
const { fetchSingleNote } = handleHospital();
const { returnBillings } = handleHospitalBilling();
const { fetchAndGeneratePdf } = salesReturnHelper();

const $route = useRoute();
const $router = useRouter();
const dateHelper = useDate();

const companyId = $route.params.companyId;
const loader = ref(true);
const salesAccountHeads = ref([]);
const totals = ref({});
const company = ref({});
const guest = ref({});

let formData = ref({
    company_id: companyId,
    contact_profile_id: null,
    account_head_id: null,
    ipd_register_id: null,
    vat_payable_account_head_id: null,
    mop_account_head_id: null,
    receipt_reference: null,
    receipt_description: 'Hotel bill payment',
    payment: null,
    project_id: null,
    cost_centre_id: null,
    location_id: null,
    discount_head_id: null,
    adjustment_amount: 0,
    status: 'active',
    bill_number: '',
    date: '',
    has_item_detail: true,
    description: '',
    order_number: null,
    sales_person_id: null,
    commission_percent: null,
    lc_number: null,
    item_details: []
});

provide('formData', formData);
provide('totals', totals);
const showSuccess = inject('showSuccess');
const showError = inject('showError');

let tableItems = reactive([]);

const ipdRegisterId = computed(() => formData.value.ipd_register_id);

watch(ipdRegisterId, (newValue) => {
    if (! newValue) return;
    const companyQuery = `?company_id=${companyId}`;
    fetchSingleNote(companyQuery, newValue).then(res => {
        if (! res.data) return;
        guest.value = res.data;
        formData.value.contact_profile_id = res.data.patient.id;
        formData.value.ipd_register_id = res.data.id;
        formData.value.service_resource_id = res.data.human_resource_id;
    });
});

const formatGeneralItems = (generalItems) => {
    return generalItems.map(td => {
        return {
            id: td.id,
            product_id: td.product_id,
            name: td.product.name,
            option_name: td.product.name,
            description: td.description,
            quantity: td.quantity,
            prev_return_qty: td.prev_return_qty,
            rate: td.rate,
            discount: td.discount,
            sale_general_id: td.id,
            vat: td.vat,
            sub_total: td.sub_total,
            discount_amount: td.discount_amount,
            discount_percent: td.discount_percent,
            vat_amount: td.vat_amount,
            total: td.total,
            is_edit: false,
            product: { name: td.product.name },
            amount: td.amount,
            total_amount: td.total_amount
        };
    });
};

const setReceiptInfo = (receiptGenerals, paidAmount) => {
    if (! receiptGenerals) return;
    if (! receiptGenerals[0]) return;
    formData.value.money_receipt_no = receiptGenerals[0].receipt_master.voucher_no;
    formData.value.mop_account_head_id = receiptGenerals[0].receipt_master.account_head_id;
    formData.value.receipt_master_id = receiptGenerals[0].receipt_master.id;
    formData.value.receipt_reference = receiptGenerals[0].receipt_master.receipt_reference;
    formData.value.total_return_amount = paidAmount;
};

const setDiscountInfo = (discountLedger) => {
    if (! discountLedger) return;
    formData.value.discount_head_id = discountLedger.account_head_id;
    formData.value.adjustment_amount = discountLedger.debit;
};

const resolveAdjustmentAmount = (amount) => {
    if (amount === null) return 0;
    if (amount === undefined) return 0;
    return amount;
};

const resolveSaleType = (saleType) => {
    if (! saleType) return 'ipd_invoice';
    return saleType;
};

const resolveReturnQty = (returnQty) => {
    if (! returnQty) return 0;
    return parseInt(returnQty);
};

onMounted(() => {
    const companyQuery = `?company_id=${companyId}`;
    const salesBillQuery = `${companyQuery}&return_data=1`;

    Promise.all([
        rest.fetchAccountReceivable(companyQuery).then(res => {
            if (res.data) formData.value.account_head_id = res.data[0].id;
        }),
        rest.fetchVatPayable(companyQuery).then(res => {
            if (res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
        }),
        fetchCompanyInfo(companyId).then(res => {
            if (res.data) company.value = res.data;
        }),
        rest.getAccountHeadBySlag('sales_accounts', companyQuery).then(res => {
            if (res.data) salesAccountHeads.value = res.data;
        }),
        fetchSalesBill($route.params.invoiceId, salesBillQuery).then(res => {
            if (! res.data) return;
            const { id, date, discount_ledger, general, receipt_generals } = res.data;
            formData.value = res.data;
            formData.value.sale_master_id = id;
            formData.value.adjustment_amount = resolveAdjustmentAmount(res.data.adjustment_amount);
            formData.value.invoice_date = date;
            formData.value.date = dateHelper.currentDate();
            tableItems.push(...formatGeneralItems(general));
            setReceiptInfo(receipt_generals, res.data.paid_amount);
            setDiscountInfo(discount_ledger);
        })
    ]).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loader.value = false;
    });
});

const resolveListRouteName = (redirectRoute) => {
    if (! redirectRoute) return 'hotel-bill-statement';
    return redirectRoute;
};

const goToList = () => {
    if ($route.query.back_url) {
        $router.push(decodeURIComponent($route.query.back_url));
        return;
    }
    const { redirect_route, ...routeQuery } = $route.query;
    $router.push({
        name: resolveListRouteName(redirect_route),
        params: $route.params,
        query: routeQuery
    });
};

const formatReturnItem = (item) => {
    return {
        product_id: item.product_id,
        quantity: item.return_qty,
        rate: item.rate,
        vat: item.vat,
        discount_amount: item.discount_amount,
        discount_percent: item.discount_percent,
        vat_amount: item.vat_amount,
        amount: item.return_amount,
        total_amount: item.total_amount,
        sale_general_id: item.id,
        stock_in_hand: item.stock_in_hand,
        prev_return_qty: item.prev_return_qty,
        description: item.description,
    };
};

const isAllItemsReturned = () => {
    const pending = formData.value.item_details.find(item => {
        return resolveReturnQty(item.return_qty) !== (item.quantity - item.prev_return_qty);
    });
    if (pending) return 'partially_returned';
    return 'returned';
};

const handleSubmit = () => {
    loader.value = true;

    formData.value.item_details = tableItems;
    let copyFormData = Object.assign({}, formData.value);

    copyFormData.item_details = formData.value.item_details.map(item => formatReturnItem(item));
    copyFormData.sale_type = resolveSaleType($route.query.sale_type);
    copyFormData.receipt_type = 'ipd_invoice_return';
    copyFormData.return_type = isAllItemsReturned();
    copyFormData.adjustment_amount = formData.value.return_roundup_discount;

    returnBillings(copyFormData).then(res => {
        if (! res.status) {
            return showError(res.message);
        }
        showSuccess(res.message);
        fetchAndGeneratePdf(company.value, res.data.id, 'ipd_invoice');
        goToList();
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loader.value = false;
    });
};
</script>

<style scoped>
address {
    margin-bottom: 0 !important;
}
</style>
